import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
  Heading,
  HStack,
  Spinner,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Icon,
  SimpleGrid,
} from "@chakra-ui/react";
import { ArrowBackIcon, EditIcon, AddIcon, RepeatIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import payrollService, {
  formatPayrollCurrency,
} from "../modules/payroll/payrollService";

const DEFAULT_META = {
  money: {
    currency: "INR",
    storageUnit: "paise",
    displayUnit: "rupees",
    scale: 100,
  },
};

const getCalculationSummary = (component) => {
  if (!component) return "-";

  if (component.calculationType === "FORMULA") {
    return component.formula || "Formula-based";
  }

  if (component.calculationType === "FIXED") {
    return formatPayrollCurrency(component.value || 0, DEFAULT_META);
  }

  if (component.calculationType === "PERCENT_OF_BASIC") {
    return `${component.value || 0}% of Basic`;
  }

  if (component.calculationType === "PERCENT_OF_CTC") {
    return `${component.value || 0}% of CTC`;
  }

  return "-";
};

const getDisplayComponents = (assignment) => {
  const breakdown = assignment?.breakdown;
  if (!breakdown) {
    return [];
  }

  return [...(breakdown.earnings || []), ...(breakdown.deductions || [])];
};

const CompensationAssignmentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [meta, setMeta] = useState(DEFAULT_META);
  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const result = await payrollService.getCompensation(id);
        setAssignment(result.data);
        setMeta(result.meta || DEFAULT_META);
      } catch (error) {
        toast({
          title: "Unable to load compensation assignment",
          description:
            error?.response?.data?.message || error.message || "Unknown error",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, toast]);

  const overrideRows = useMemo(() => {
    const variables = assignment?.variables || {};
    return Object.entries(variables).map(([key, value]) => ({ key, value }));
  }, [assignment]);

  const structureComponents = useMemo(
    () => getDisplayComponents(assignment),
    [assignment],
  );

  if (loading) {
    return (
      <VStack py={16} spacing={4}>
        <Spinner size="xl" color="blue.500" />
        <Text color="gray.500">Loading compensation assignment...</Text>
      </VStack>
    );
  }

  if (!assignment) {
    return (
      <Box p={{ base: 4, md: 6 }}>
        <Stack spacing={4}>
          <Button
            alignSelf="flex-start"
            leftIcon={<ArrowBackIcon />}
            variant="outline"
            onClick={() => navigate("/payroll/ctc")}
          >
            Back to Payroll
          </Button>
          <Card>
            <CardBody>
              <Text color="gray.500">Compensation assignment not found.</Text>
            </CardBody>
          </Card>
        </Stack>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }} bg="gray.50" minH="100vh">
      <Stack spacing={4} maxW="100%" mx="auto">
        {/* ===== HEADER ===== */}
        <Stack
          spacing={{ base: 3, md: 0 }}
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          pb={{ base: 3, md: 4 }}
          borderBottom="1px"
          borderColor="gray.200"
        >
          <Stack spacing={2}>
            <HStack spacing={3} align="flex-start">
              <Button
                leftIcon={<ArrowBackIcon />}
                variant="outline"
                size={{ base: "sm", md: "md" }}
                onClick={() => navigate("/payroll/ctc")}
              >
                Back
              </Button>
              <Box>
                <Heading size={{ base: "md", md: "lg" }}>
                  {assignment.userId?.name || "Compensation Assignment"}
                </Heading>
                <Text
                  fontSize={{ base: "xs", md: "sm" }}
                  color="gray.600"
                  mt={1}
                >
                  {assignment.userId?.role || "Employee"} •{" "}
                  {assignment.salaryStructureId?.code || "Structure"}
                </Text>
              </Box>
              <Badge
                colorScheme={assignment.isActive ? "green" : "gray"}
                h="fit-content"
              >
                {assignment.isActive ? "ACTIVE" : "INACTIVE"}
              </Badge>
            </HStack>
          </Stack>

          {/* Actions */}
          <HStack spacing={{ base: 2, md: 3 }}>
            <Button
              leftIcon={<EditIcon />}
              colorScheme="blue"
              size={{ base: "sm", md: "md" }}
              onClick={() => toast({ title: "Edit assignment (coming soon)" })}
            >
              Edit
            </Button>
            <Button
              leftIcon={<AddIcon />}
              variant="outline"
              size={{ base: "sm", md: "md" }}
              onClick={() => toast({ title: "Add override (coming soon)" })}
            >
              Override
            </Button>
            <Button
              leftIcon={<RepeatIcon />}
              variant="outline"
              size={{ base: "sm", md: "md" }}
              onClick={() => toast({ title: "Recalculate (coming soon)" })}
            >
              Recalc
            </Button>
          </HStack>
        </Stack>

        {/* ===== SALARY FLOW (HERO) ===== */}
        <Card bg="white" boxShadow="md">
          <CardBody py={{ base: 4, md: 5 }}>
            <Grid
              templateColumns={{ base: "1fr", md: "1fr 1fr" }}
              gap={{ base: 4, md: 6 }}
            >
              {/* Left: Flow Steps */}
              <Stack spacing={3}>
                <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={2}>
                  Salary Transformation
                </Text>

                {/* Base Salary */}
                <Box
                  p={3}
                  bg="blue.50"
                  borderRadius="md"
                  borderLeft="4px"
                  borderLeftColor="blue.400"
                >
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    fontWeight="bold"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Base Salary (Input)
                  </Text>
                  <Text fontSize="xl" fontWeight="bold" color="blue.600">
                    {formatPayrollCurrency(assignment.baseSalary, meta)}
                  </Text>
                </Box>

                <Box textAlign="center" py={1}>
                  <Text fontSize="2xl" color="gray.300">
                    ↓
                  </Text>
                </Box>

                {/* Gross Earnings */}
                <Box
                  p={3}
                  bg="green.50"
                  borderRadius="md"
                  borderLeft="4px"
                  borderLeftColor="green.400"
                >
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    fontWeight="bold"
                    textTransform="uppercase"
                    mb={1}
                  >
                    + Gross Earnings
                  </Text>
                  <Text fontSize="xl" fontWeight="bold" color="green.600">
                    {formatPayrollCurrency(
                      assignment.breakdown?.gross || 0,
                      meta,
                    )}
                  </Text>
                </Box>

                <Box textAlign="center" py={1}>
                  <Text fontSize="2xl" color="gray.300">
                    ↓
                  </Text>
                </Box>

                {/* Deductions */}
                <Box
                  p={3}
                  bg="red.50"
                  borderRadius="md"
                  borderLeft="4px"
                  borderLeftColor="red.400"
                >
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    fontWeight="bold"
                    textTransform="uppercase"
                    mb={1}
                  >
                    - Deductions
                  </Text>
                  <Text fontSize="xl" fontWeight="bold" color="red.600">
                    {formatPayrollCurrency(
                      (assignment.breakdown?.earnings || []).reduce(
                        (sum, e) => {
                          if (e.type === "DEDUCTION")
                            return sum + (e.amount || 0);
                          return sum;
                        },
                        0,
                      ),
                      meta,
                    )}
                  </Text>
                </Box>

                <Box textAlign="center" py={1}>
                  <Text fontSize="2xl" color="gray.300">
                    ↓
                  </Text>
                </Box>

                {/* Net Pay */}
                <Box
                  p={3}
                  bg="purple.50"
                  borderRadius="md"
                  borderLeft="4px"
                  borderLeftColor="purple.400"
                >
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    fontWeight="bold"
                    textTransform="uppercase"
                    mb={1}
                  >
                    = Net Pay
                  </Text>
                  <Text fontSize="xl" fontWeight="bold" color="purple.600">
                    {formatPayrollCurrency(
                      assignment.breakdown?.net || 0,
                      meta,
                    )}
                  </Text>
                </Box>
              </Stack>

              {/* Right: Components Stacked Bar */}
              <Stack spacing={3} justify="center">
                <Text fontSize="sm" fontWeight="bold" color="gray.700">
                  Earnings Composition
                </Text>
                <Box h={{ base: "250px", md: "280px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        {
                          name: "Salary",
                          Earnings:
                            (assignment.breakdown?.earnings || [])
                              .filter((c) => c.type === "EARNING")
                              .reduce((sum, c) => sum + (c.amount || 0), 0) /
                            100,
                          Deductions:
                            (assignment.breakdown?.earnings || [])
                              .filter((c) => c.type === "DEDUCTION")
                              .reduce((sum, c) => sum + (c.amount || 0), 0) /
                            100,
                        },
                      ]}
                      margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => `₹${value.toFixed(0)}`} />
                      <Legend />
                      <Bar dataKey="Earnings" stackId="a" fill="#38a169" />
                      <Bar dataKey="Deductions" stackId="a" fill="#e53e3e" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Stack>
            </Grid>
          </CardBody>
        </Card>

        {/* ===== KEY METRICS ===== */}
        <Grid
          templateColumns={{ base: "1fr", sm: "repeat(3, 1fr)" }}
          gap={{ base: 2, md: 3 }}
        >
          <Card bg="white" boxShadow="sm">
            <CardBody py={{ base: 3, md: 4 }}>
              <Stat>
                <StatLabel fontSize={{ base: "xs", md: "sm" }}>
                  Structure Code
                </StatLabel>
                <StatNumber fontSize={{ base: "lg", md: "xl" }}>
                  {assignment.salaryStructureId?.code || "-"}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card bg="white" boxShadow="sm">
            <CardBody py={{ base: 3, md: 4 }}>
              <Stat>
                <StatLabel fontSize={{ base: "xs", md: "sm" }}>
                  Effective Since
                </StatLabel>
                <StatNumber fontSize={{ base: "sm", md: "md" }}>
                  {assignment.effectiveFrom
                    ? new Date(assignment.effectiveFrom).toLocaleDateString(
                        "en-IN",
                        { month: "short", year: "numeric" },
                      )
                    : "-"}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card bg="white" boxShadow="sm">
            <CardBody py={{ base: 3, md: 4 }}>
              <Stat>
                <StatLabel fontSize={{ base: "xs", md: "sm" }}>
                  Component Overrides
                </StatLabel>
                <StatNumber
                  fontSize={{ base: "lg", md: "xl" }}
                  color={overrideRows.length > 0 ? "orange.600" : "gray.600"}
                >
                  {overrideRows.length}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </Grid>

        {/* ===== SALARY BREAKDOWN ===== */}
        <Card bg="white">
          <CardHeader pb={3}>
            <Heading size={{ base: "sm", md: "md" }}>Salary Breakdown</Heading>
          </CardHeader>
          <CardBody>
            <TableContainer
              overflowX={{ base: "auto", md: "visible" }}
              sx={{
                "&::-webkit-scrollbar": { height: "4px" },
                "&::-webkit-scrollbar-track": { bg: "gray.100" },
                "&::-webkit-scrollbar-thumb": {
                  bg: "gray.400",
                  borderRadius: "2px",
                },
              }}
            >
              <Table
                size={{ base: "xs", md: "sm" }}
                minW={{ base: "650px", md: "100%" }}
              >
                <Thead>
                  <Tr bg="gray.50">
                    <Th fontSize={{ base: "xs", md: "sm" }}>Component</Th>
                    <Th fontSize={{ base: "xs", md: "sm" }}>Type</Th>
                    <Th fontSize={{ base: "xs", md: "sm" }}>Rule</Th>
                    <Th fontSize={{ base: "xs", md: "sm" }} isNumeric>
                      Amount
                    </Th>
                    <Th fontSize={{ base: "xs", md: "sm" }} textAlign="center">
                      Source
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {structureComponents.map((component, idx) => (
                    <Tr
                      key={component.key}
                      bg={idx % 2 === 0 ? "white" : "gray.50"}
                      borderLeft={
                        component.overridden
                          ? "3px solid #ed8936"
                          : "3px solid transparent"
                      }
                    >
                      <Td fontWeight="600" fontSize={{ base: "xs", md: "sm" }}>
                        {component.key}
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={
                            component.type === "DEDUCTION" ? "red" : "green"
                          }
                          fontSize="xs"
                        >
                          {component.type === "DEDUCTION"
                            ? "Deduction"
                            : "Earning"}
                        </Badge>
                      </Td>
                      <Td fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                        {getCalculationSummary(component)}
                      </Td>
                      <Td
                        isNumeric
                        fontSize={{ base: "xs", md: "sm" }}
                        fontWeight="500"
                      >
                        {formatPayrollCurrency(component.amount, meta)}
                      </Td>
                      <Td textAlign="center">
                        <Badge
                          colorScheme={component.overridden ? "orange" : "blue"}
                          fontSize="xs"
                        >
                          {component.overridden ? "Override" : "Structure"}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>

        {/* ===== OVERRIDES (HIGHLIGHTED) ===== */}
        <Card
          bg={overrideRows.length > 0 ? "orange.50" : "white"}
          borderLeft={overrideRows.length > 0 ? "4px solid #ed8936" : "none"}
        >
          <CardHeader pb={3}>
            <HStack justify="space-between" align="center">
              <Heading size={{ base: "sm", md: "md" }}>
                Employee-Specific Overrides
              </Heading>
              {overrideRows.length > 0 && (
                <Badge colorScheme="orange" fontSize="sm">
                  {overrideRows.length} Active
                </Badge>
              )}
            </HStack>
          </CardHeader>
          <CardBody>
            {overrideRows.length === 0 ? (
              <VStack spacing={3} py={4}>
                <Text color="gray.500">No component overrides are set.</Text>
                <Text fontSize="sm" color="gray.500">
                  This assignment uses the salary structure defaults.
                </Text>
                <Button
                  size="sm"
                  colorScheme="orange"
                  onClick={() => toast({ title: "Add override (coming soon)" })}
                  mt={2}
                >
                  + Add Override
                </Button>
              </VStack>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {overrideRows.map((row) => (
                  <Box
                    key={row.key}
                    p={3}
                    borderRadius="md"
                    bg="white"
                    borderLeft="3px solid #ed8936"
                  >
                    <HStack justify="space-between" mb={2}>
                      <Text fontWeight="600" fontSize="sm">
                        {row.key}
                      </Text>
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() =>
                          toast({ title: "Edit override (coming soon)" })
                        }
                      >
                        Edit
                      </Button>
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                      Override Value
                    </Text>
                    <Text fontSize="lg" fontWeight="bold" color="orange.600">
                      {formatPayrollCurrency(row.value, meta)}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </CardBody>
        </Card>

        {/* ===== EMPLOYEE CONTEXT ===== */}
        <Card bg="white">
          <CardHeader pb={3}>
            <Heading size={{ base: "sm", md: "md" }}>Employee Details</Heading>
          </CardHeader>
          <CardBody>
            <Grid
              templateColumns={{
                base: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              }}
              gap={4}
            >
              <Box>
                <Text
                  fontSize="xs"
                  color="gray.600"
                  fontWeight="bold"
                  textTransform="uppercase"
                  mb={1}
                >
                  Employee ID
                </Text>
                <Text fontWeight="600">
                  {assignment.userId?.employeeId || "-"}
                </Text>
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  color="gray.600"
                  fontWeight="bold"
                  textTransform="uppercase"
                  mb={1}
                >
                  Email
                </Text>
                <Text fontWeight="600" fontSize="sm">
                  {assignment.userId?.email || "-"}
                </Text>
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  color="gray.600"
                  fontWeight="bold"
                  textTransform="uppercase"
                  mb={1}
                >
                  Role
                </Text>
                <Text fontWeight="600">{assignment.userId?.role || "-"}</Text>
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  color="gray.600"
                  fontWeight="bold"
                  textTransform="uppercase"
                  mb={1}
                >
                  Structure Name
                </Text>
                <Text fontWeight="600">
                  {assignment.salaryStructureId?.name || "-"}
                </Text>
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  color="gray.600"
                  fontWeight="bold"
                  textTransform="uppercase"
                  mb={1}
                >
                  Effective From
                </Text>
                <Text fontWeight="600">
                  {assignment.effectiveFrom
                    ? new Date(assignment.effectiveFrom).toLocaleDateString()
                    : "-"}
                </Text>
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  color="gray.600"
                  fontWeight="bold"
                  textTransform="uppercase"
                  mb={1}
                >
                  Effective To
                </Text>
                <Text fontWeight="600">
                  {assignment.effectiveTo
                    ? new Date(assignment.effectiveTo).toLocaleDateString()
                    : "Open-ended"}
                </Text>
              </Box>
            </Grid>
          </CardBody>
        </Card>

        {/* ===== CALCULATION CONTEXT (COLLAPSED) ===== */}
        <Accordion allowToggle>
          <AccordionItem
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
            bg="white"
          >
            <AccordionButton py={3}>
              <Box
                as="span"
                flex="1"
                textAlign="left"
                fontSize={{ base: "sm", md: "md" }}
                fontWeight="600"
              >
                Calculation Logic
              </Box>
            </AccordionButton>
            <AccordionPanel pb={4} pt={3}>
              <Grid
                templateColumns={{
                  base: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                }}
                gap={4}
              >
                <Box>
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    fontWeight="bold"
                    textTransform="uppercase"
                    mb={1}
                  >
                    BASIC (used in formulas)
                  </Text>
                  <Text fontWeight="600">
                    {formatPayrollCurrency(
                      assignment.breakdown?.context?.BASIC || 0,
                      meta,
                    )}
                  </Text>
                </Box>
                <Box>
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    fontWeight="bold"
                    textTransform="uppercase"
                    mb={1}
                  >
                    BASE_SALARY (input)
                  </Text>
                  <Text fontWeight="600">
                    {formatPayrollCurrency(
                      assignment.breakdown?.context?.BASE_SALARY || 0,
                      meta,
                    )}
                  </Text>
                </Box>
                <Box>
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    fontWeight="bold"
                    textTransform="uppercase"
                    mb={1}
                  >
                    CTC (used in formulas)
                  </Text>
                  <Text fontWeight="600">
                    {formatPayrollCurrency(
                      assignment.breakdown?.context?.CTC || 0,
                      meta,
                    )}
                  </Text>
                </Box>
              </Grid>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Stack>
    </Box>
  );
};

export default CompensationAssignmentDetailPage;
